import { getDocument } from '@atlaskit/browser-apis';

export const WINDOW_MS: number = 30 * 24 * 60 * 60 * 1000; // ±30 days

// Returns the parsed target time, or midnight UTC today when none is given.
// The fallback must be stable across renders so Relay doesn't refetch.
/* eslint-disable-next-line @atlaskit/platform/one-value-export-per-file -- legacy onboarding baseline */
export const resolveTargetTimeMs = (
	targetTimeIso?: string | null,
	now: Date = new Date(),
): number => {
	if (targetTimeIso) {
		return Date.parse(targetTimeIso);
	}
	return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
};

// Linked-Google-account events within ±WINDOW_MS of the target event start.
export const CYPHER_QUERY = `
	MATCH (user:IdentityUser {ari: $me})
		-[:user_linked_third_party_user]->(tpu:IdentityThirdPartyUser)
		-[:user_attended_calendar_event]->(event:ExternalCalendarEvent)
	WHERE event.eventStartTime >= datetime($startMin)
		AND event.eventStartTime <= datetime($startMax)
	RETURN event
	LIMIT 100
`;

// Reads cloudId from the `ajs-cloud-id` meta tag (Confluence runtime),
// falling back to a `?cloudId=…` URL param (AFM examples).
/* eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports, @atlaskit/platform/one-value-export-per-file -- legacy onboarding baseline */
export const getCloudIdFromRuntime = (): string => {
	const doc = getDocument();
	if (typeof window === 'undefined' || !doc) {
		return '';
	}
	const metaCloudId = doc.querySelector('meta[name="ajs-cloud-id"]')?.getAttribute('content');
	if (metaCloudId) {
		return metaCloudId;
	}
	return new URLSearchParams(window.location.search).get('cloudId') ?? '';
};

export type FetchedGoogleCalendarEvent = {
	id: string;
	url: string | null;
	title: string;
	startTimeIso: string | null;
	endTimeIso: string | null;
};

// Loosely-typed shape of the Relay query response that
// `mapCalendarEventsResponse` consumes. Defined here (instead of importing the
// generated Relay type) so this module has zero coupling to Relay artifacts —
// the actual query lives co-located with its consuming hook in
// `GoogleCalendarCard.tsx`.
export type CalendarEventsResponseLike = {
	graphStore?: {
		cypherQueryV2?: {
			edges?: ReadonlyArray<
				| {
						node?: {
							columns?: ReadonlyArray<
								| {
										value?: {
											__typename?: string;
											data?: {
												__typename?: string;
												id?: string;
												displayName?: string | null;
												eventStartTime?: string | null;
												eventEndTime?: string | null;
												url?: string | null;
											} | null;
										} | null;
								  }
								| null
								| undefined
							>;
						} | null;
				  }
				| null
				| undefined
			>;
		} | null;
	} | null;
};

// Maps a Relay calendar-events response to a flat event list, skipping
// non-`ExternalCalendarEvent` entries.
/* eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports, @atlaskit/platform/one-value-export-per-file -- legacy onboarding baseline */
export const mapCalendarEventsResponse = (
	data: CalendarEventsResponseLike | null | undefined,
): FetchedGoogleCalendarEvent[] => {
	const edges = data?.graphStore?.cypherQueryV2?.edges ?? [];

	return edges
		.flatMap((edge) => edge?.node?.columns ?? [])
		.map((column): FetchedGoogleCalendarEvent | null => {
			if (!column || column.value?.__typename !== 'GraphStoreCypherQueryV2AriNode') return null;
			const entity = column.value.data;
			if (entity?.__typename !== 'ExternalCalendarEvent' || !entity.id) return null;
			return {
				id: entity.id,
				url: entity.url ?? null,
				title: entity.displayName ?? '',
				startTimeIso: entity.eventStartTime ?? null,
				endTimeIso: entity.eventEndTime ?? null,
			};
		})
		.filter((event): event is FetchedGoogleCalendarEvent => event !== null);
};
